import {Component, EventEmitter, Inject, Input, NgZone, OnInit, Output} from '@angular/core';
import {NGXLogger} from 'ngx-logger';
import {Router, RouterModule} from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertService, AUTH_SVC, AuthService, CookieService, UserService } from '@company-box/services';
import { User } from '@company-box/models';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: "app-login-modal",
  templateUrl: "./login-modal.component.html",
  styleUrls: ["./login-modal.component.scss"],
  imports: [CommonModule, MatInputModule, MatFormFieldModule, FormsModule, RouterModule, MatTooltipModule],
})
export class LoginModalComponent implements OnInit {
  public forgotPasswordActive = false;
  recoveryEmail: any;
  loginData = {
    username: "",
    firstName: "",
    lastName: "",
    password: "",
  };
  public registerData: User = new User();

  public hasLowerLetter = false;
  public hasCapitalLetter = false;
  public hasNumber = false;
  public hasSpecialChar = false;
  public hasMinimumChars = false;
  public passwordsMatch = false;
  public passwordTouched = false;

  public rush: boolean = false;

  options: string = "";

  public passwordInfo = false;

  public accessDenied: boolean = true;
  public sent: boolean = false;

  public activeLoginModalTab = "login";
  loginError: string | undefined;
  signupError: string | undefined;
  signupFieldErrors: Record<string, string> = {};
  loginPending = false;
  sendPending = false;
  signupPending = false;

  @Input() reload = true;
  @Input() action: string = "";
  @Input() productData: any;
  @Output() registerSuccess: EventEmitter<any> = new EventEmitter();
  @Output() emailSuccess: EventEmitter<any> = new EventEmitter();
  @Output() loginSuccess: EventEmitter<any> = new EventEmitter();

  constructor(
    private userService: UserService,
    @Inject(AUTH_SVC) private authService: AuthService,
    private alertSvc: AlertService,
    private logger: NGXLogger,
    private router: Router,
    private cookieService: CookieService,
    public zone: NgZone
  ) {}

  ngOnInit() {
    if (window.screen.width < 993) {
      this.passwordInfo = true;
    }
    const isTest = JSON.parse(this.cookieService.read("isTest") || "false");

    if (isTest) {
      const productDetails = this.readProductCookie();
      this.loginData.username = productDetails.username;
      this.loginData.password = productDetails.password;
      this.cookieService.deleteWithDomain("isTest");
    }
  }
  readProductCookie() {
    const encodedData = this.cookieService.read("productDetails");
    if (encodedData) {
      const productDetails = JSON.parse(atob(encodedData));
      this.cookieService.deleteWithDomain("productDetails");
      return productDetails;
    }
    return undefined;
  }

  getActiveTab(tab: string) {
    if (this.activeLoginModalTab === tab) {
      return "active";
    } else {
      return "inactive";
    }
  }

  login(): Promise<boolean> {
    if (this.loginPending) {
      return Promise.resolve(false);
    }
    this.loginError = undefined;
    this.loginPending = true;
    return this.authService
      .authorize(this.loginData)
      .then((success) => {
        if (this.reload) {
          window.location.reload();
        }

        if (this.action) {
          this.router.navigate([this.action]);
        }
        this.loginSuccess.emit();
        return true;
      })
      .catch((error) => {
        this.logger.debug("Login failed", error.message);
        this.loginError = error.message || error.error_description;
        return false;
      })
      .finally(() => {
        this.loginPending = false;
      });
  }

  send() {
    if (this.sendPending) {
      return;
    }
    this.sent = false;
    this.sendPending = true;
    this.userService
      .recoverPassword(this.recoveryEmail)
      .then(
        (success) => {
          this.sent = true;
          this.emailSuccess.emit();
        },
        (error) => {
          this.logger.debug("Recover failed", error.message);
          this.alertSvc.showError({ msg: "Unable to send recover password email" });
          this.sent = false;
        }
      )
      .finally(() => {
        this.sendPending = false;
      });
  }

  createUser() {
    if (this.signupPending) {
      return;
    }
    this.signupError = undefined;
    this.signupFieldErrors = {};
    this.signupPending = true;
    this.userService
      .createUser(this.registerData)
      .then(() => {
        this.loginData.username = this.registerData.username as string;
        this.loginData.password = this.registerData.password as string;
        this.loginData.firstName = this.registerData.firstName as string;
        this.loginData.lastName = this.registerData.lastName as string;
        this.registerSuccess.emit();
        return this.login();
      })
      .catch((error) => {
        if (error?.fields?.length) {
          for (const f of error.fields) {
            const field = f.name?.split('.').pop();
            if (field) {
              this.signupFieldErrors[field] = f.message;
            }
          }
        } else {
          this.signupError = error?.message || (typeof error === 'string' ? error : 'Sign up failed. Please try again.');
        }
      })
      .finally(() => {
        this.signupPending = false;
      });
  }

  validatePassword(password: any = "", confirmation: any = this.registerData.passwordConfirmation) {
    this.passwordTouched = password.length > 0;
    this.hasLowerLetter = false;
    this.hasCapitalLetter = false;
    this.hasNumber = false;
    this.hasSpecialChar = false;
    this.hasMinimumChars = password.length >= 8 ? true : false;
    this.passwordsMatch = password === confirmation;

    if (password.match(/\d/)) {
      this.hasNumber = true;
    }

    if (password.match(/[a-z]/)) {
      this.hasLowerLetter = true;
    }

    if (password.match(/[A-Z]/)) {
      this.hasCapitalLetter = true;
    }

    if (password.match(/[@!#$^%&*()+=\-\[\]\\';,.\/{}|":<>? ]/)) {
      this.hasSpecialChar = true;
    }

    if (
      this.hasLowerLetter &&
      this.hasCapitalLetter &&
      this.hasMinimumChars &&
      this.hasNumber &&
      this.hasSpecialChar &&
      this.passwordsMatch
    ) {
      return true;
    }
    return false;
  }

  showPasswordInfo() {
    this.passwordInfo = true;
  }

  hidePasswordInfo() {
    this.passwordInfo = false;
  }
}
